import React from 'react';
import { MapPin, Phone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Premium Reusable Document Template Component
 * Used for: Quotation, Sales Invoice, Purchase Order, Delivery Challan, Proforma
 * 
 * Props:
 * - documentType: 'QUOTATION' | 'SALES_INVOICE' | 'PURCHASE_ORDER' | 'DELIVERY_CHALLAN' | 'PROFORMA'
 * - document: Document data object
 * - business: Business data object
 * - templateSettings: Template customization settings (logo, fonts, colors, visibility)
 */
const DocumentTemplate = ({ documentType, document, business, templateSettings = {} }) => {
  // Extract settings from new template structure or fall back to old format
  const settings = {
    logo: templateSettings?.branding?.logo_url || templateSettings.logo || null,
    letterhead: templateSettings?.branding?.letterhead_url || null,
    fontFamily: templateSettings?.typography?.font_family || templateSettings.fontFamily || 'Inter',
    primaryColor: templateSettings?.theme?.primary_color || templateSettings.primaryColor || '#3B82F6',
    secondaryColor: templateSettings?.theme?.secondary_color || '#8B5CF6',
    showGSTIN: templateSettings?.toggles?.show_gstin !== undefined ? templateSettings.toggles.show_gstin : 
               (templateSettings.showGSTIN !== false),
    showHSN: templateSettings?.toggles?.show_hsn !== undefined ? templateSettings.toggles.show_hsn :
             (templateSettings.showHSN !== false),
    showDiscount: templateSettings?.toggles?.show_tax_breakup !== undefined ? templateSettings.toggles.show_tax_breakup :
                  (templateSettings.showDiscount !== false),
    showTax: templateSettings?.toggles?.show_tax_breakup !== undefined ? templateSettings.toggles.show_tax_breakup :
             (templateSettings.showTax !== false),
    showSignature: templateSettings?.toggles?.show_signature !== undefined ? templateSettings.toggles.show_signature : true,
    showBankDetails: templateSettings?.toggles?.show_bank_details !== undefined ? templateSettings.toggles.show_bank_details : true,
    footerText: templateSettings?.defaults?.default_notes || templateSettings.footerText || 'This is a computer generated document',
    headerStyle: templateSettings?.layout?.header_style || 'standard',
    tableDensity: templateSettings?.layout?.table_density || 'comfortable',
    zebraRows: templateSettings?.layout?.zebra_rows || false,
    showBorders: templateSettings?.layout?.show_borders !== undefined ? templateSettings.layout.show_borders : true
  };

  // Document type display names
  const documentTypeNames = {
    QUOTATION: 'QUOTATION',
    SALES_INVOICE: 'SALES INVOICE',
    PURCHASE_ORDER: 'PURCHASE ORDER',
    DELIVERY_CHALLAN: 'DELIVERY CHALLAN',
    PROFORMA: 'PROFORMA INVOICE'
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div 
      className="bg-white" 
      style={{ 
        fontFamily: settings.fontFamily,
        width: '210mm',
        minHeight: '297mm',
        margin: '0 auto',
        padding: '15mm'
      }}
    >
      {/* HEADER SECTION */}
      <div className="grid grid-cols-2 gap-8 mb-8 pb-6" style={{ borderBottom: `3px solid ${settings.primaryColor}` }}>
        {/* Left: Company Info */}
        <div>
          {settings.logo && (
            <img src={settings.logo} alt="Company Logo" className="mb-4 h-16 object-contain" />
          )}
          <h1 className="text-3xl font-bold text-gray-900 mb-1" style={{ letterSpacing: '0.5px' }}>
            {business.business_name || business.name || 'COMPANY NAME'}
          </h1>
          {business.tagline && (
            <p className="text-base text-gray-600 mb-4">{business.tagline}</p>
          )}
          
          <div className="space-y-1.5 text-sm text-gray-700">
            {settings.showGSTIN && business.gstin && (
              <p className="font-medium">GSTIN: {business.gstin}</p>
            )}
            {business.address && (
              <>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: settings.primaryColor }} />
                  <div>
                    {business.address.line1 && <p>{business.address.line1}</p>}
                    {business.address.city && (
                      <p>{business.address.city}, {business.address.state} {business.address.pincode}</p>
                    )}
                  </div>
                </div>
                {business.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 flex-shrink-0" style={{ color: settings.primaryColor }} />
                    <p>{business.phone}</p>
                  </div>
                )}
              </>
            )}
            {business.email && <p>📧 {business.email}</p>}
          </div>
        </div>

        {/* Right: Document Info */}
        <div className="text-right">
          <h2 className="text-3xl font-bold mb-6" style={{ color: settings.primaryColor, letterSpacing: '1px' }}>
            {documentTypeNames[documentType] || 'DOCUMENT'}
          </h2>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="font-semibold text-gray-700">
                {documentType === 'QUOTATION' ? 'Quotation' : 
                 documentType === 'SALES_INVOICE' ? 'Invoice No' :
                 documentType === 'PURCHASE_ORDER' ? 'PO Number' : 'Document No'}:
              </span>
              <span className="font-bold text-gray-900">{document.doc_no}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold text-gray-700">Date:</span>
              <span className="text-gray-900">{formatDate(document.doc_date)}</span>
            </div>
            {document.meta?.valid_until && documentType === 'QUOTATION' && (
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Valid Until:</span>
                <span className="text-gray-900">{formatDate(document.meta.valid_until)}</span>
              </div>
            )}
            {document.meta?.due_date && (
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Due Date:</span>
                <span className="text-gray-900">{formatDate(document.meta.due_date)}</span>
              </div>
            )}
            {document.meta?.reference_no && (
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Ref No:</span>
                <span className="text-gray-900">{document.meta.reference_no}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PARTY INFORMATION */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-gray-600 mb-3 uppercase tracking-wide">Bill To</h3>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <p className="font-bold text-lg text-gray-900 mb-2">{document.party.name}</p>
          <div className="grid grid-cols-2 gap-x-6 text-sm text-gray-700">
            {document.party.gstin && settings.showGSTIN && (
              <p><span className="font-medium">GSTIN:</span> {document.party.gstin}</p>
            )}
            {document.party.phone && (
              <p><span className="font-medium">Phone:</span> {document.party.phone}</p>
            )}
          </div>
          {document.party.billing_address && (
            <div className="mt-2 text-sm text-gray-700">
              <p className="font-medium text-gray-800">Address:</p>
              <p>{document.party.billing_address.line1}</p>
              {document.party.billing_address.city && (
                <p>{document.party.billing_address.city}, {document.party.billing_address.state} {document.party.billing_address.pincode}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ITEMS TABLE */}
      <table className="w-full mb-6 text-sm">
        <thead>
          <tr className="border-y-2 border-gray-300" style={{ backgroundColor: '#F3F4F6' }}>
            <th className="py-3 px-3 text-left font-bold text-gray-700 uppercase tracking-wide">#</th>
            <th className="py-3 px-3 text-left font-bold text-gray-700 uppercase tracking-wide">Item Description</th>
            {settings.showHSN && (
              <th className="py-3 px-3 text-left font-bold text-gray-700 uppercase tracking-wide">HSN/SAC</th>
            )}
            <th className="py-3 px-3 text-right font-bold text-gray-700 uppercase tracking-wide">Qty</th>
            <th className="py-3 px-3 text-right font-bold text-gray-700 uppercase tracking-wide">Rate</th>
            {settings.showDiscount && (
              <th className="py-3 px-3 text-right font-bold text-gray-700 uppercase tracking-wide">Disc</th>
            )}
            {settings.showTax && (
              <th className="py-3 px-3 text-right font-bold text-gray-700 uppercase tracking-wide">Tax %</th>
            )}
            <th className="py-3 px-3 text-right font-bold text-gray-700 uppercase tracking-wide">Amount</th>
          </tr>
        </thead>
        <tbody>
          {document.items && document.items.map((item, index) => (
            <tr key={index} className="border-b border-gray-200">
              <td className="py-3 px-3 text-gray-700">{item.line_no}</td>
              <td className="py-3 px-3">
                <div>
                  <p className="font-semibold text-gray-900">{item.name}</p>
                  {item.description && (
                    <p className="text-xs text-gray-600 mt-0.5">{item.description}</p>
                  )}
                  {item.batch_no && (
                    <p className="text-xs text-gray-500 mt-0.5">Batch: {item.batch_no}</p>
                  )}
                </div>
              </td>
              {settings.showHSN && (
                <td className="py-3 px-3 text-gray-700">{item.hsn_sac || '-'}</td>
              )}
              <td className="py-3 px-3 text-right text-gray-900">
                {item.qty} {item.unit}
              </td>
              <td className="py-3 px-3 text-right text-gray-900">{formatCurrency(item.price)}</td>
              {settings.showDiscount && (
                <td className="py-3 px-3 text-right text-gray-700">
                  {item.discount_value > 0 ? (
                    item.discount_type === 'PERCENT' ? `${item.discount_value}%` : formatCurrency(item.discount_value)
                  ) : '-'}
                </td>
              )}
              {settings.showTax && (
                <td className="py-3 px-3 text-right text-gray-900">{item.tax_rate}%</td>
              )}
              <td className="py-3 px-3 text-right font-semibold text-gray-900">{formatCurrency(item.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* BOTTOM SECTION: Notes + Totals */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        {/* Left: Notes & Terms */}
        <div>
          {document.terms?.notes_public && (
            <div className="mb-4">
              <h4 className="text-sm font-bold text-gray-700 mb-2">Notes</h4>
              <p className="text-sm text-gray-600 whitespace-pre-line">{document.terms.notes_public}</p>
            </div>
          )}
          
          {document.terms?.terms_lines && document.terms.terms_lines.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-700 mb-2">Terms & Conditions</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {document.terms.terms_lines.map((term, idx) => (
                  <li key={idx}>• {term}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right: Totals */}
        <div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium text-gray-900">{formatCurrency(document.totals.subtotal)}</span>
            </div>
            
            {document.totals.discount_total > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Discount</span>
                <span>- {formatCurrency(document.totals.discount_total)}</span>
              </div>
            )}

            {document.charges && document.charges.length > 0 && (
              <>
                {document.charges.map((charge, idx) => (
                  <div key={idx} className="flex justify-between text-green-600">
                    <span>{charge.name}</span>
                    <span>+ {formatCurrency(charge.amount)}</span>
                  </div>
                ))}
              </>
            )}
            
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium text-gray-700">Taxable Amount</span>
              <span className="font-semibold text-gray-900">{formatCurrency(document.totals.taxable_amount)}</span>
            </div>

            {/* Tax Breakup */}
            {document.tax_breakup && document.tax_breakup.length > 0 && (
              <>
                {document.tax_breakup.map((tax, idx) => {
                  const isSingleTax = tax.tax_rate === 0 || !tax.tax_rate;
                  const cgstRate = tax.tax_rate / 2;
                  const sgstRate = tax.tax_rate / 2;
                  const cgstAmount = tax.tax / 2;
                  const sgstAmount = tax.tax / 2;

                  if (document.meta?.place_of_supply !== business.address?.state) {
                    // IGST for inter-state
                    return (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-600">IGST @ {tax.tax_rate}%</span>
                        <span className="text-gray-900">{formatCurrency(tax.tax)}</span>
                      </div>
                    );
                  } else {
                    // CGST + SGST for intra-state
                    return (
                      <React.Fragment key={idx}>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">CGST @ {cgstRate}%</span>
                          <span className="text-gray-900">{formatCurrency(cgstAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">SGST @ {sgstRate}%</span>
                          <span className="text-gray-900">{formatCurrency(sgstAmount)}</span>
                        </div>
                      </React.Fragment>
                    );
                  }
                })}
              </>
            )}

            {document.totals.round_off !== 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Round Off</span>
                <span className="text-gray-900">
                  {document.totals.round_off >= 0 ? '+' : ''}{formatCurrency(Math.abs(document.totals.round_off))}
                </span>
              </div>
            )}

            {/* Grand Total */}
            <div 
              className="flex justify-between border-t-2 pt-3 mt-2"
              style={{ borderColor: settings.primaryColor }}
            >
              <span className="text-base font-bold text-gray-900">Grand Total</span>
              <span className="text-lg font-bold text-gray-900">{formatCurrency(document.totals.grand_total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* BANK DETAILS SECTION */}
      {settings.showBankDetails !== false && business.bank_account && (
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-bold text-gray-700 mb-3">Bank Details</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {business.bank_account.account_name && (
              <div>
                <span className="text-gray-600">Account Name:</span>
                <p className="font-medium text-gray-900">{business.bank_account.account_name}</p>
              </div>
            )}
            {business.bank_account.account_number && (
              <div>
                <span className="text-gray-600">Account Number:</span>
                <p className="font-medium text-gray-900">{business.bank_account.account_number}</p>
              </div>
            )}
            {business.bank_account.ifsc && (
              <div>
                <span className="text-gray-600">IFSC Code:</span>
                <p className="font-medium text-gray-900">{business.bank_account.ifsc}</p>
              </div>
            )}
            {business.bank_account.bank_name && (
              <div>
                <span className="text-gray-600">Bank Name:</span>
                <p className="font-medium text-gray-900">{business.bank_account.bank_name}</p>
              </div>
            )}
            {business.bank_account.branch && (
              <div>
                <span className="text-gray-600">Branch:</span>
                <p className="font-medium text-gray-900">{business.bank_account.branch}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FOOTER SECTION */}
      <div className="mt-12 pt-6 border-t border-gray-300">
        <div className="grid grid-cols-2 gap-8">
          {/* Left: Company Name */}
          <div>
            <p className="text-sm font-semibold text-gray-700">For</p>
            <p className="text-base font-bold text-gray-900 mt-1">
              {business.business_name || business.name}
            </p>
          </div>

          {/* Right: Signature */}
          {settings.showSignature !== false && (
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-700 mb-8">
                For {business.business_name || business.name}
              </p>
              <div className="border-t border-gray-400 pt-2 inline-block min-w-[200px]">
                <p className="text-sm font-semibold text-gray-900">Authorized Signatory</p>
                {document.signature?.authorized_name && (
                  <p className="text-xs text-gray-600 mt-1">{document.signature.authorized_name}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Computer Generated Text + QR Codes */}
        <div className="flex items-center justify-between mt-8">
          <div className="flex-1">
            <p className="text-xs text-gray-400">{settings.footerText}</p>
          </div>
          
          {/* QR Codes Section */}
          {(settings?.toggles?.show_doc_qr || settings?.toggles?.show_upi_qr) && (
            <div className="flex gap-4 items-start ml-4">
              {settings.toggles.show_doc_qr && document?.id && (
                <div className="text-center">
                  <QRCodeSVG
                    value={`${window.location.origin}/quotations/${document.id}`}
                    size={80}
                    level="M"
                    includeMargin={false}
                  />
                  <p className="text-xs text-gray-600 mt-1">Scan to View</p>
                </div>
              )}
              
              {settings.toggles.show_upi_qr && business?.upi_id && (
                <div className="text-center">
                  <QRCodeSVG
                    value={`upi://pay?pa=${business.upi_id}&pn=${encodeURIComponent(business.name || '')}&am=${document?.totals?.grand_total || 0}&cu=INR`}
                    size={80}
                    level="M"
                    includeMargin={false}
                  />
                  <p className="text-xs text-gray-600 mt-1">Pay via UPI</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentTemplate;
